// ArChat Auto-Normalized File
// Created: 2026-03-27
// @file NotificationContext.jsx
// Campanita por contacto (vista contactos) + notificaciones in-app persistidas (DB → WS → UI).

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useMessagesStore } from './MessagesContext';
import { request } from '../services/apiClient';
import { API_BASE } from '../utils/constants';
import { subscribeIncomingChatMessage } from '../utils/incomingMessageBridge';
import { incomingMessageSoundEngine } from '../modules/notifications/incomingMessageSoundEngine';

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification debe usarse dentro de NotificationProvider');
  }
  return context;
};

function notificationsApiUrl(suffix) {
  const base = (API_BASE && API_BASE.startsWith('http'))
    ? API_BASE.replace(/\/$/, '')
    : (typeof window !== 'undefined'
        ? (window.location.origin + (API_BASE.startsWith('/') ? API_BASE : `/${API_BASE}`))
        : API_BASE).replace(/\/$/, '');
  return `${base}/notifications/${suffix}`;
}

/** Tenant para TenantGuard: usuario autenticado tiene prioridad sobre localStorage. */
function notificationRequestHeaders(user) {
  let tid = user?.tenant_id ?? user?.tenantId ?? null;
  if (tid == null || tid === '') {
    try {
      tid = typeof localStorage !== 'undefined' ? localStorage.getItem('archat_tenant_id') : null;
    } catch (_) {
      tid = null;
    }
  }
  if (tid == null || tid === '') return {};
  return { 'X-Tenant-ID': String(tid) };
}

function isLocalRuntime() {
  if (typeof window === 'undefined') return false;
  const host = String(window.location?.hostname ?? '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const { activeConversationId } = useMessagesStore();
  const userId = user?.id ?? user?.usuario_id ?? null;

  const [notifiedContacts, setNotifiedContacts] = useState(new Set());
  const [notificationTimestamps, setNotificationTimestamps] = useState(new Map());

  const [inAppNotifications, setInAppNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  /** Invitaciones pendientes (promociones) — misma fuente que MainApp/getSentInvitations vía evento */
  const [pendingInvitesCount, setPendingInvitesCount] = useState(0);
  const [pendingInvitesTitle, setPendingInvitesTitle] = useState('');
  /** Tras abrir la fila sintética en la campanita, no sumar pending al badge hasta que cambie el conteo real del sync. */
  const [pendingInvitesSeenForBadge, setPendingInvitesSeenForBadge] = useState(false);
  const prevPendingSyncCountRef = useRef(null);
  const imsLogEnabled = import.meta.env.DEV || isLocalRuntime();

  useEffect(() => {
    incomingMessageSoundEngine.bootstrapDevBridge();
  }, []);

  useEffect(() => {
    const soundEnabled =
      userId != null && Number(user?.notification_sound_enabled ?? 1) !== 0;
    if (imsLogEnabled) {
      console.log('[IMS_PREF_STATE]', {
        userId,
        notification_sound_enabled: user?.notification_sound_enabled ?? null,
        soundEnabled,
      });
    }
    incomingMessageSoundEngine.setCurrentUserId(userId);
    incomingMessageSoundEngine.setSoundEnabled(soundEnabled);
  }, [userId, user?.notification_sound_enabled, imsLogEnabled]);

  useEffect(() => {
    incomingMessageSoundEngine.setActiveConversationId(activeConversationId);
  }, [activeConversationId]);

  const activeConvRef = useRef(activeConversationId);
  useEffect(() => { activeConvRef.current = activeConversationId; }, [activeConversationId]);

  useEffect(() => {
    const unsubscribe = subscribeIncomingChatMessage((detail) => {
      if (imsLogEnabled) {
        console.log('[BEEP_TRACE_EVENT]', {
          source: 'bridge',
          conversation_id:
            detail?.conversation_id ??
            detail?.conversacion_id ??
            detail?.conversationId ??
            detail?.cid ??
            null,
          message_id:
            detail?.id ??
            detail?.message_id ??
            detail?.mensaje_id ??
            detail?.temp_id ??
            null,
          sender_id:
            detail?.sender_id ??
            detail?.remitente_id ??
            detail?.emisor_id ??
            detail?.usuario_id ??
            null,
          activeConversationId: activeConvRef.current || null,
        });
      }
      incomingMessageSoundEngine.notifyIncomingMessage(detail);
    });
    return unsubscribe;
  }, [imsLogEnabled]);

  // `publishIncomingChatMessage` se emite desde socketService junto a `archat:new_message`
  // (no depende del ciclo de render de MessagesContext). Un único listener aquí → IMS.

  useEffect(() => {
    const onSeen = (event) => {
      const detail = event?.detail || {};
      const cid =
        detail.conversationId ?? detail.conversation_id ?? detail.conversacion_id;
      if (imsLogEnabled) {
        console.log('[IMS_STOP_REASON]', {
          reason: 'conversation_seen_event',
          conversationId: cid ?? null,
          interacted: detail.interacted === true,
          force: detail.force === true,
        });
        if (detail.force === true) {
          console.log('[IMS_OPEN_CONVERSATION_STOP]', {
            conversationId: cid ?? null,
            interacted: detail.interacted === true,
            force: true,
          });
        }
      }
      incomingMessageSoundEngine.markConversationSeen(cid, {
        interacted: detail.interacted === true,
        force: detail.force === true,
      });
    };
    window.addEventListener('archat:conversation_seen', onSeen);
    return () => window.removeEventListener('archat:conversation_seen', onSeen);
  }, [imsLogEnabled]);

  // StrictMode: el cleanup del mount simulado no debe vaciar pendientes IMS (mata beep repetitivo).
  useEffect(() => {
    incomingMessageSoundEngine.resumeLoopIfPendingAfterMount();
    return () => {
      incomingMessageSoundEngine.pauseLoopForProviderUnmount();
    };
  }, []);

  const clearNotification = useCallback((contactId) => {
    setNotifiedContacts((prev) => {
      const next = new Set(prev);
      next.delete(String(contactId));
      return next;
    });
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    if (!token || !userId) return;
    try {
      const res = await request(notificationsApiUrl('unread_count.php'), {
        method: 'GET',
        headers: notificationRequestHeaders(user),
      });
      if (!res.ok) return;
      const j = await res.json();
      if (j && typeof j.unread_count === 'number') {
        setUnreadCount(j.unread_count);
      }
    } catch (_) {}
  }, [token, userId]);

  const fetchNotifications = useCallback(async () => {
    if (!token || !userId) return;
    try {
      const res = await request(notificationsApiUrl('list.php?limit=30'), { method: 'GET' });
      if (!res.ok) return;
      const j = await res.json();
      if (j?.notifications && Array.isArray(j.notifications)) {
        setInAppNotifications(j.notifications);
      }
    } catch (_) {}
  }, [token, userId, user]);

  useEffect(() => {
    const onPendingInvitesSync = (ev) => {
      const d = ev?.detail || {};
      const c = Number(d.count);
      if (!Number.isFinite(c) || c < 0) return;
      const n = Math.max(0, Math.floor(c));
      const prev = prevPendingSyncCountRef.current;
      if (prev !== null && prev !== n) {
        setPendingInvitesSeenForBadge(false);
      }
      prevPendingSyncCountRef.current = n;
      setPendingInvitesCount(n);
      if (n === 0) {
        setPendingInvitesTitle('');
        setPendingInvitesSeenForBadge(false);
        prevPendingSyncCountRef.current = null;
        return;
      }
      if (typeof d.title === 'string' && d.title.trim() !== '') {
        setPendingInvitesTitle(d.title.trim());
      }
    };
    window.addEventListener('archat:pending-invitations-sync', onPendingInvitesSync);
    return () => window.removeEventListener('archat:pending-invitations-sync', onPendingInvitesSync);
  }, []);

  useEffect(() => {
    if (!userId || !token) {
      setInAppNotifications([]);
      setUnreadCount(0);
      setPendingInvitesCount(0);
      setPendingInvitesTitle('');
      setPendingInvitesSeenForBadge(false);
      prevPendingSyncCountRef.current = null;
      return;
    }
    void fetchNotifications();
    void refreshUnreadCount();
  }, [userId, token, fetchNotifications, refreshUnreadCount]);

  useEffect(() => {
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible' && userId && token) {
        void fetchNotifications();
        void refreshUnreadCount();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [userId, token, fetchNotifications, refreshUnreadCount]);

  const addNotificationRealtime = useCallback(
    (payload) => {
      if (!payload || payload.id == null) return;
      setInAppNotifications((prev) => {
        const exists = prev.some((n) => n.id === payload.id);
        if (exists) return prev;
        const row = {
          id: payload.id,
          tenant_id: payload.tenant_id,
          user_id: payload.user_id,
          type: payload.type || 'generic',
          title: payload.title || '',
          body: payload.body || '',
          data: payload.data && typeof payload.data === 'object' ? payload.data : {},
          is_read: !!payload.is_read,
          created_at: payload.created_at,
          read_at: payload.read_at ?? null,
        };
        return [row, ...prev].slice(0, 50);
      });
      const unread = !Number(payload.is_read);
      if (unread) {
        setUnreadCount((c) => c + 1);
      }
    },
    []
  );

  useEffect(() => {
    const handler = (ev) => {
      const d = ev?.detail;
      if (d) addNotificationRealtime(d);
    };
    window.addEventListener('archat:inapp_notification', handler);
    return () => window.removeEventListener('archat:inapp_notification', handler);
  }, [addNotificationRealtime]);

  const notificationsForDisplay = useMemo(() => {
    const rows = [];
    if (pendingInvitesCount > 0 && pendingInvitesTitle) {
      rows.push({
        id: '__archat_pending_invites__',
        synthetic: true,
        type: 'pending_invitations',
        title: pendingInvitesTitle,
        body: '',
        data: { pending_count: pendingInvitesCount },
        is_read: pendingInvitesSeenForBadge,
        created_at: null,
        read_at: pendingInvitesSeenForBadge ? new Date().toISOString() : null,
      });
    }
    return [...rows, ...(Array.isArray(inAppNotifications) ? inAppNotifications : [])];
  }, [pendingInvitesCount, pendingInvitesTitle, pendingInvitesSeenForBadge, inAppNotifications]);

  /** Campanita: no sumar pending de promociones si ya hay fila no leída en DB (mismo tipo) — evita 1+1=2. */
  const displayUnreadCount = useMemo(() => {
    const hasSynthetic =
      pendingInvitesCount > 0 && String(pendingInvitesTitle || '').trim() !== '';
    if (!hasSynthetic) return unreadCount;
    const unreadPendingInDb = inAppNotifications.filter(
      (n) => !n.is_read && String(n.type || '') === 'pending_invitations'
    ).length;
    if (unreadPendingInDb > 0) return unreadCount;
    if (pendingInvitesSeenForBadge) return unreadCount;
    return unreadCount + pendingInvitesCount;
  }, [unreadCount, pendingInvitesCount, pendingInvitesTitle, pendingInvitesSeenForBadge, inAppNotifications]);

  const acknowledgePendingInvitesInBell = useCallback(() => {
    setPendingInvitesSeenForBadge(true);
  }, []);

  const markAsRead = useCallback(
    async (id) => {
      if (id === '__archat_pending_invites__') return;
      const nid = Number(id);
      if (!Number.isFinite(nid) || nid <= 0 || !token) return;
      setInAppNotifications((prev) =>
        prev.map((n) => (n.id === nid ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
      );
      try {
        const res = await request(notificationsApiUrl('read.php'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...notificationRequestHeaders(user),
          },
          body: JSON.stringify({ id: nid }),
        });
        if (res.ok) {
          void refreshUnreadCount();
        } else {
          void fetchNotifications();
          void refreshUnreadCount();
        }
      } catch (_) {
        void fetchNotifications();
        void refreshUnreadCount();
      }
    },
    [token, user, refreshUnreadCount, fetchNotifications]
  );

  const markConversationSeen = useCallback((conversationId, options = {}) => {
    return incomingMessageSoundEngine.markConversationSeen(conversationId, options);
  }, []);

  const stopIncomingMessageSound = useCallback((reason = 'manual') => {
    incomingMessageSoundEngine.stopAll(reason);
  }, []);

  const value = {
    notifiedContacts,
    notificationTimestamps,
    clearNotification,
    inAppNotifications,
    notificationsForDisplay,
    unreadCount,
    displayUnreadCount,
    fetchNotifications,
    refreshUnreadCount,
    acknowledgePendingInvitesInBell,
    markAsRead,
    addNotificationRealtime,
    markConversationSeen,
    stopIncomingMessageSound,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
